import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  FormControl,
  FormErrorIcon,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Icon,
  Input,
  Text,
  useControllableState,
  VStack,
} from '@chakra-ui/react';
import { Copy as ChainLink } from '../../assets';
import { Notification } from '../notification';
import { getDataTracking } from '../../utils';

interface SaveUrlStepProps {
  customUrl: string;
  errorMessage?: string;
  headingLabel?: string;
  isError: boolean;
  isSaving: boolean;
  onSave: (url: string) => void;
  urlPrefix: string;
}

export const SaveUrlStep: FC<SaveUrlStepProps> = (props) => {
  const {
    urlPrefix,
    isSaving,
    onSave,
    customUrl,
    isError,
    errorMessage,
    headingLabel,
  } = props;

  // Treat the url input as both controlled and uncontrolled
  // It's controlled because we only want to accept valid url characters
  const [value, setValue] = useState(customUrl);
  const [internalValue, setInternalValue] = useControllableState({
    value,
    onChange: setValue,
  });

  const promptVerbiage = `You can create a personalized web address for your personal fundraising page to make it easier for your family and friends to find.`;
  const hints = `Page URL may only contain letters, numbers, dashes (-) or underscores (_)`;

  /**
   * Permit dash, underscore, alphanumeric only
   * @param url
   * @returns boolean
   */
  const urlPattern = '^([a-zA-Z]|[0-9]|[_]|[-])*$';
  const isValid = (url: string): boolean => RegExp(urlPattern).test(url);
  const requiredValidationMessage = 'A URL is required';

  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
  } = useForm();

  const onSaveUrl = () => {
    if (internalValue !== '') {
      onSave(internalValue);
    } else {
      // This is to support quirky behavior where submitting the form on enter key
      // is bypassing the validation. Therefore, capture it here via a custom error.
      setError('customUrl', {
        type: 'string',
        message: requiredValidationMessage,
      });
    }
  };

  return (
    <VStack spacing={6} alignItems="flex-start">
      <VStack color="brand.primary.swatch1" textAlign="center">
        <Icon as={ChainLink} boxSize="48px" />
        <Heading fontSize={24}>{headingLabel}</Heading>
      </VStack>

      <Text>{promptVerbiage}</Text>
      <Text>{urlPrefix}</Text>

      <form onSubmit={handleSubmit(onSaveUrl)}>
        <VStack spacing={4}>
          <FormControl isInvalid={!!errors.customUrl}>
            <FormLabel htmlFor="customUrl">
              Enter your custom page URL
            </FormLabel>
            <Input
              id="customUrl"
              {...register('customUrl', {
                required: requiredValidationMessage,
              })}
              value={internalValue}
              onChange={(e) => {
                const url = e.target.value;
                if (isValid(url)) {
                  setInternalValue(url);
                }
              }}
            />
            <FormErrorMessage>
              <>
                <FormErrorIcon />
                {errors.customUrl?.message}
              </>
            </FormErrorMessage>
            <FormHelperText>{hints}</FormHelperText>
          </FormControl>

          {isError && (
            <Notification status="error" message={errorMessage ?? ''} />
          )}

          <Button
            type="submit"
            colorScheme="blue"
            w="100%"
            isLoading={isSaving}
            size="lg"
            {...getDataTracking('saveCustomUrlButton')}
          >
            Save custom URL
          </Button>
        </VStack>
      </form>
    </VStack>
  );
};
